<vectronic-card
  [title]="getTitle()"
  [displayCollarDeploymentInfographic]="true"
  [displayClose]="true"
  (closed)="dialogRef.close()"
>
  <ng-container body>
    <mat-dialog-content
      class="create-deployment__content"
      [ngClass]="{ 'three-columns': form.get('closeDeployment')?.value }"
    >
      <form [formGroup]="form">
        <!-- Column 1: Animal/Collar Info -->
        <div class="create-deployment__column">
          @if (data.collar) {
            <p class="label"><b>Collar: </b>{{ data.collar?.name ?? data.collar?.id }}</p>
          }
          @if (data.animal && data.animal?.name) {
            <p class="label mb-12"><b>Animal: </b>{{ data.animal?.name }}</p>
          }

          @if (data.type === 'assignCollar' || data.type === 'add') {
            @if (!data.collar) {
              <mat-form-field appearance="outline">
                <mat-label>Select Collar:</mat-label>
                <mat-select
                  formControlName="collarId"
                  (selectionChange)="onCollarSelectionChange($event)"
                  required
                >
                  @for (collar of data.collars; track collar) {
                    <mat-option [value]="collar.id">{{ collar.name }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
            }
          }

          @if (data.type === 'assignAnimal') {
            @if (!data.animal) {
              <mat-form-field appearance="outline">
                <mat-label>Select Animal:</mat-label>
                <mat-select
                  formControlName="animalId"
                  (selectionChange)="onAnimalSelectionChange($event)"
                  required
                >
                  <mat-option [value]="0">Create new Animal</mat-option>
                  @for (animal of data.animals; track animal) {
                    <mat-option [value]="animal.id">{{ animal.name }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
            }
          }

          <div class="in-a-row">
            <mat-form-field appearance="outline">
              <mat-label>Species</mat-label>
              <mat-select formControlName="species" (selectionChange)="onSpeciesChange($event)">
                <mat-option [value]="undefined"></mat-option>
                @for (speciesOption of data.animalSpeciesOptions; track speciesOption) {
                  <mat-option [value]="speciesOption">{{
                    speciesTranslator(speciesOption)
                  }}</mat-option>
                }
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Name</mat-label>
              <input matInput formControlName="name" required />
            </mat-form-field>
          </div>

          <div class="in-a-row">
            <mat-form-field appearance="outline" class="sex-field">
              <mat-label>Sex</mat-label>
              <mat-select formControlName="sex">
                @for (sexOption of sexOptions; track sexOption) {
                  <mat-option [value]="sexOption">{{ sexTranslator(sexOption) }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
            <div class="create-deployment__color">
              <mat-label>Color:</mat-label>
              <vectronic-color-picker
                [initialColor]="form.get('selectedColor')?.value"
                (colorChanged)="onColorSelected($event)"
              ></vectronic-color-picker>
            </div>
          </div>

          <div class="in-a-row">
            <mat-form-field appearance="outline" class="datepicker">
              <mat-label>Date of Birth</mat-label>
              <input
                matInput
                [matDatepicker]="dobPicker"
                formControlName="dateOfBirth"
                [max]="maxDate"
                [matDatepickerFilter]="dateOfBirthFilter"
              />
              <mat-datepicker-toggle matSuffix [for]="dobPicker"></mat-datepicker-toggle>
              <mat-datepicker #dobPicker></mat-datepicker>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Date of Death</mat-label>
              <input
                matInput
                [matDatepicker]="deathDatePicker"
                formControlName="dateOfDeath"
                [max]="maxDate"
                [matDatepickerFilter]="dateOfDeathFilter"
              />
              <mat-datepicker-toggle matSuffix [for]="deathDatePicker"></mat-datepicker-toggle>
              <mat-datepicker #deathDatePicker></mat-datepicker>
            </mat-form-field>
          </div>

          <mat-form-field appearance="outline" class="textarea">
            <mat-label>Death Comment</mat-label>
            <textarea matInput formControlName="deathComment"></textarea>
          </mat-form-field>

          <mat-form-field appearance="outline" class="textarea">
            <mat-label>Description</mat-label>
            <textarea matInput formControlName="description"></textarea>
          </mat-form-field>

          <mat-form-field appearance="outline" class="textarea">
            <mat-label>Personal Notes</mat-label>
            <textarea matInput formControlName="personalNotes"></textarea>
          </mat-form-field>
        </div>

        <!-- Column 2: Deployment Info -->
        <div class="create-deployment__column">
          <div class="deployment-range">
            <mat-form-field class="datepicker" appearance="outline">
              <mat-label>Start Deployment</mat-label>
              <input
                matInput
                [matDatepicker]="startDate"
                formControlName="startDate"
                [matDatepickerFilter]="dateFilterStart"
                [max]="maxDate"
                (focus)="startDate.open()"
              />
              <mat-datepicker-toggle matSuffix [for]="startDate"></mat-datepicker-toggle>
              <mat-datepicker #startDate></mat-datepicker>
            </mat-form-field>
          </div>
          <div class="in-a-row">
            <mat-form-field appearance="outline">
              <mat-label>Age</mat-label>
              <mat-select formControlName="age">
                @for (ageOption of ageOptions; track ageOption) {
                  <mat-option [value]="ageOption">{{ getAgeTranslation(ageOption) }}</mat-option>
                }
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Weight (kg)</mat-label>
              <input
                matInput
                type="text"
                inputmode="decimal"
                formControlName="weight"
                (keydown)="preventInvalidCharacters($event)"
              />
            </mat-form-field>
          </div>

          <div class="in-a-row">
            <mat-form-field appearance="outline">
              <mat-label>Start Latitude</mat-label>
              <input
                matInput
                type="text"
                inputmode="decimal"
                formControlName="latitude"
                (keydown)="preventInvalidCharacters($event)"
              />
              @if (form.get('latitude')?.value < -90 || form.get('latitude')?.value > 90) {
                <mat-error>Range: -90 to 90</mat-error>
              }
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Start Longitude</mat-label>
              <input
                matInput
                type="text"
                inputmode="decimal"
                formControlName="longitude"
                (keydown)="preventInvalidCharacters($event)"
              />
              @if (form.get('longitude')?.value < -180 || form.get('longitude')?.value > 180) {
                <mat-error>Range: -180 to 180</mat-error>
              }
            </mat-form-field>
          </div>

          <mat-form-field appearance="outline">
            <mat-label>Deploy on Person</mat-label>
            <input matInput formControlName="deployPerson" />
          </mat-form-field>

          <mat-form-field appearance="outline" class="textarea">
            <mat-label>Deployment Comment</mat-label>
            <textarea formControlName="deployComment" matInput></textarea>
          </mat-form-field>

          @if (!data.animal) {
            <mat-checkbox
              formControlName="shareWithCollarUsers"
              matTooltip="Shares the new animal with all users who already have permission to the associated collar."
            >
              Share with collar users
            </mat-checkbox>
          }

          <div class="close-deployment-checkbox">
            <mat-checkbox formControlName="closeDeployment"> Close Deployment </mat-checkbox>
          </div>
        </div>

        <!-- Column 3: End Deployment Fields (only shown if closeDeployment is true) -->
        <div class="create-deployment__column">
          @if (form.get('closeDeployment')?.value) {
            <mat-form-field class="datepicker" appearance="outline">
              <mat-label>End Deployment</mat-label>
              <input
                matInput
                [matDatepicker]="endDate"
                formControlName="endDate"
                [matDatepickerFilter]="dateFilterEnd"
                [max]="maxDate"
                (focus)="endDate.open()"
              />
              <mat-datepicker-toggle matSuffix [for]="endDate"></mat-datepicker-toggle>
              <mat-datepicker #endDate></mat-datepicker>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Deployment End Type</mat-label>
              <mat-select formControlName="endType">
                @for (endTypeOption of endTypeOptions; track endTypeOption) {
                  <mat-option [value]="endTypeOption">{{
                    getEndTypeTranslation(endTypeOption)
                  }}</mat-option>
                }
              </mat-select>
            </mat-form-field>

            <div class="coordinates">
              <mat-form-field appearance="outline">
                <mat-label>End Latitude</mat-label>
                <input
                  matInput
                  type="text"
                  inputmode="decimal"
                  formControlName="endLatitude"
                  (keydown)="preventInvalidCharacters($event)"
                />
                @if (form.get('endLatitude')?.value < -90 || form.get('endLatitude')?.value > 90) {
                  <mat-error>Range: -90 to 90</mat-error>
                }
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>End Longitude</mat-label>
                <input
                  matInput
                  type="text"
                  inputmode="decimal"
                  formControlName="endLongitude"
                  (keydown)="preventInvalidCharacters($event)"
                />
                @if (
                  form.get('endLongitude')?.value < -180 || form.get('endLongitude')?.value > 180
                ) {
                  <mat-error>Range: -180 to 180</mat-error>
                }
              </mat-form-field>
            </div>

            <mat-form-field appearance="outline">
              <mat-label>Deploy off Person</mat-label>
              <input matInput formControlName="endPerson" />
            </mat-form-field>
          }
        </div>
      </form>
    </mat-dialog-content>

    <mat-dialog-actions class="modal-button-container action-buttons">
      <button mat-button [mat-dialog-close]="null" color="warn">
        <mat-icon>cancel</mat-icon>
        Cancel
      </button>
      <span
        class="submit-tooltip-wrapper"
        matTooltipPosition="above"
        [matTooltip]="getSubmitTooltip()"
      >
        <button
          mat-button
          color="accent"
          (click)="onSubmit()"
          [disabled]="!this.form.valid || !form.dirty"
        >
          <mat-icon>send</mat-icon>
          {{ getButtonTitle() }}
        </button>
      </span>
    </mat-dialog-actions>
  </ng-container>
</vectronic-card>
